import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as path from "node:path";

import {
  DEFAULT_STORE,
  WorkspaceLayout,
  type ArtifactStore,
} from "./infra/storage.js";

export const EXIT_OK = 0;
export const EXIT_USAGE = 64;
export const EXIT_INPUT = 66;
export const EXIT_RUNTIME = 70;
export const EXIT_NEEDS_AGENT = 78;

export const DEFAULT_CONCURRENCY = 30;
// direct init fans out many small, independent batch/title extractions; it runs
// hotter than the generic default. Other commands keep DEFAULT_CONCURRENCY.
export const DEFAULT_DIRECT_CONCURRENCY = 80;
export const DEFAULT_BATCH_TARGET_LINES = 36;
export const DEFAULT_BATCH_MAX_CHARS = 1800;
export const DEFAULT_BATCH_MIN_LINES = 12;
export const DEFAULT_BATCH_MODE = "episode";
// Whole-script synopsis reduce: if the concatenated episode synopses fit within
// this many chars, reduce them in one LLM call; otherwise group + merge in
// multiple levels. DEFAULT_SUMMARY_GROUP_SIZE is the fallback episodes-per-group
// cap when char-based grouping still needs a hard bound.
export const DEFAULT_SUMMARY_REDUCE_THRESHOLD_CHARS = 6000;
export const DEFAULT_SUMMARY_GROUP_SIZE = 8;

export const DIRECT_REL = path.join("draft", "scriptctl", "direct");
export const SCRIPT_REL = path.join("workspace", "script.json");
export const SUPPORTED_EXTS = ".txt/.md/.docx/.xlsx/.json";
export const DIRECT_CONTRACT_VERSION = 3;
export const SCRIPT_SCHEMA_VERSION = 2;

export const WORLDVIEW_VALUES = [
  "上古时代",
  "古代历史",
  "古风架空",
  "近代民国",
  "现代",
  "欧洲中世纪",
  "赛博朋克",
  "星际时代",
  "西幻架空",
];

export const SCRIPT_TARGET_KINDS = new Set(["actor", "location", "prop"]);
export const DELIVERY_VALUES = new Set(["single", "simultaneous", "overlap", "group"]);
export const EFFECTIVE_FROM_VALUES = new Set(["before_action", "after_action"]);

// Spec for the subagent-driven `scriptctl parse` workflow. Unlike direct batch
// extraction, the 正文 md carries ONLY the
// screenplay and registers no assets; each asset KIND lives in its own file
// (人物 / 场景 / 道具 / 发声源), plus an optional 梗概.md for the whole-script
// synopsis. Many subagents work in parallel — body md never collide; each asset
// file is a shared registry consolidated + de-duplicated at parse time.
export const PARSE_MD_SPEC =
  "scriptctl parse 输入 = 一个 md 工作区目录。文件按职责分开，互不混写：\n" +
  "\n" +
  "  ep_001.md, ep_002.md, ...   正文（每集一个，只放剧本本身）\n" +
  "  人物.md                     全剧唯一一份角色登记（所有 worker 共同往里加）\n" +
  "  场景.md                     全剧唯一一份地点登记\n" +
  "  道具.md                     全剧唯一一份道具登记\n" +
  "  发声源.md                   全剧唯一一份非人物发声源登记\n" +
  "  梗概.md                     （可选）整部全文梗概\n" +
  "\n" +
  "🔴 铁律：正文 md 只管正文，绝不在正文里登记/输出任何资产。资产一律登记到上面对应\n" +
  "   的 人物/场景/道具/发声源 md 里。正文里只“引用”名字与已登记状态，不写描述。\n" +
  "🔴 资产就这 4 份，全剧共用、大家一起加；同名只登一次，重复无妨（parse / 合并阶段去重）。\n" +
  "🟡 只有正文、还没写资产 md？先跑 `scriptctl parse <dir> --extract-assets`：它从正文里\n" +
  "   引用到的资产名出发，用 LLM 补出描述 / 世界观 / 主角，写回上面这几份资产 md 供你复核；\n" +
  "   复核微调后再跑 `scriptctl parse <dir>`（纯确定性）装配 script.json。\n" +
  "\n" +
  "==================== 1. 正文 md（每集一个，ep_<NNN>.md） ====================\n" +
  "# <本集标题>            ← 第一行，非「剧本/梗概」的顶级标题即本集标题；缺省时 parse 从正文生成短标题\n" +
  "\n" +
  "## 梗概\n" +
  "<本集一段话梗概，纯文本，可多行>\n" +
  "\n" +
  "# 剧本\n" +
  "\n" +
  "## 场景 1\n" +
  "- 时空: <内|外> <日|夜>\n" +
  "- 地点: <地点名>[场景状态]            ← 只写名字，引用 场景.md 里登记的地点/状态\n" +
  "- 角色: <角色1>, <角色2>(状态), <角色3>  ← 只写名字，引用 人物.md 里登记的角色/状态\n" +
  "- 道具: <道具1>, <道具2>(状态), <道具3>[状态] ← 只写名字，引用 道具.md 里登记的道具/状态\n" +
  "\n" +
  "> [act] 动作或环境描写。\n" +
  "> [dlg|name] 角色对白原文。\n" +
  "> [dlg|name|emotion] 带情绪标签的对白。\n" +
  "> [dlg|system:<发声源名>] 非人物发声源对白。\n" +
  "> [dlg|prop:<道具名>] 道具发声对白。\n" +
  "> [dlg|broadcast:<广播名>] 广播 / 喇叭发声对白。\n" +
  "> [dlg|<speaker1>/<speaker2>|simultaneous] 多人同时说同一句。\n" +
  "> [dlg|overlap] <speaker1>:<line1> / <speaker2>:<line2>\n" +
  "> [think|name] 角色心声/内心独白。\n" +
  "\n" +
  "## 场景 2\n" +
  "...\n" +
  "\n" +
  "资产 md 的格式：每个资产一个顶级 bullet，它的“状态”（外观造型档位）写成缩进的子 bullet。\n" +
  "  - <资产名>: <身份/描述>\n" +
  "    - <状态名>: <这个状态下的具体可视外观>\n" +
  "    - <状态名>: ...\n" +
  "（也接受扁平写法 `- <资产名>|<状态名>: 描述`；两种都行，最后合并去重。）\n" +
  "正文未显式写状态时，parse 会自动绑定该资产的 `默认` 状态；没有 `默认` 且只有一个状态时，绑定唯一状态。\n" +
  "多个非默认状态无法确定时保持 state_id:null，并由 validate 给出 warning。\n" +
  "\n" +
  "==================== 2. 人物.md（角色登记 + 状态） ====================\n" +
  "# 人物\n" +
  "- <角色名>: <角色身份/外貌描述>     ← 每个会在正文出现的角色都要登记且有非空描述\n" +
  "  - 默认: <这个角色默认形象的具体外观>\n" +
  "  - <状态名>: <换装/造型后的具体外观>   ← 持久外观造型档位（整套着装/发型/持续妆造）\n" +
  "\n" +
  "==================== 3. 场景.md（地点登记 + 状态） ====================\n" +
  "# 场景\n" +
  "- <地点名>: <地点用途/氛围>\n" +
  "  - 默认: <默认环境外观>\n" +
  "  - <状态名>: <例如 白天/夜晚/被毁后 的外观>\n" +
  "\n" +
  "==================== 4. 道具.md（道具登记 + 状态） ====================\n" +
  "# 道具\n" +
  "- <道具名>: <道具描述>\n" +
  "  - 默认: <默认外观>\n" +
  "  - <状态名>: <例如 破损/发光 的外观>\n" +
  "\n" +
  "==================== 5. 发声源.md（非人物发声源登记，无状态） ====================\n" +
  "# 发声源\n" +
  "- <发声源名>(system): <声音描述>\n" +
  "- <广播名>(broadcast)\n" +
  "- <群体名>(group)\n" +
  "\n" +
  "==================== 6. 梗概.md（可选，整部全文梗概） ====================\n" +
  "# 梗概\n" +
  "<整部全文梗概，纯文本，可多行>\n" +
  "\n" +
  "==================== 7. 元信息.md（可选，整部元信息；一般由 merger 收口时写） ====================\n" +
  "worldview: <世界观，取这些枚举之一：" + WORLDVIEW_VALUES.join(" / ") + ">\n" +
  "style: <风格，自由文本，如 都市甜宠 / 悬疑复仇>\n" +
  "title: <剧名>\n" +
  "主角: <主角名1>, <主角名2>   ← 列在这里的设为 role_type=主角，其余角色=配角\n" +
  "\n" +
  "Rules:\n" +
  "- 正文 `- 角色/地点/道具:` 和对白 anchor 用的名字，必须是对应 md 里登记的规范名。\n" +
  "- 🔴 状态要抽全：凡是某角色/地点/道具在剧中出现可辨的持久外观档位（换装、发型、显著\n" +
  "  伤妆、场景白天/夜晚/被毁等），都要在其下登一个状态子 bullet；并在正文场景 `- 角色:`\n" +
  "  行用 `名(状态名)` 引用当集所处状态，没写=默认。给每个资产至少登一个 `默认` 状态。\n" +
  "- 状态描述只写稳定可视外观，不写镜头/剧情调度；不要把“背对镜头、正脸未露、特写、\n" +
  "  初始、转身、接过手、拥吻、站在舞台上”等动作、遮挡、机位或情节词写进状态描述。\n" +
  "- 每个状态可在下一行写 `提取缘由:`，用自然语言说明原文证据和为什么它是独立可复用\n" +
  "  外观状态；不要写结构化标签。连续变化过程帧、局部光效强弱、透明/消散/凝实进度、\n" +
  "  昏迷卧床、普通便装默认不作为独立状态。\n" +
  "- 明确仪式身份可保守补造型状态：婚礼里位于红毯尽头、接过新娘手、舞台拥吻的新郎，\n" +
  "  应登记“新郎造型/婚礼正装”状态；整套服装造型属于人物状态，不作为道具。\n" +
  "- 资产名是规范实体名，不夹括号 / 状态 / 情绪 / 身份注解；情绪 / 姿态 / 临时脏污伤口 /\n" +
  "  处境优先放进 action 文本或 dlg 的 emotion 标签，parse/direct 不会按关键词自动删除\n" +
  "  已显式登记的人物状态。\n" +
  "- 场景名不要写成「夜晚的教室 / 黄昏的街道」这种状态化资产；登记为「教室 / 街道」，把\n" +
  "  夜晚 / 黄昏写成该场景的状态。parse/direct 会对确定的时间状态做保守规范化。\n" +
  "- 人物持有或佩戴的物件应显式登记 / 引用为道具，不要写成人物状态；parse/direct 不会\n" +
  "  从人物名 / 人物引用 / 人物状态标签自动拆出道具。\n" +
  "- 道具是需要作为独立视觉资产追踪的具体物件，不是动作里出现的所有物品名。只有推动\n" +
  "  剧情、承载规则 / 证据 / 秘密 / 任务、需要看清特定文字 / 图案 / 照片 / 界面、跨场\n" +
  "  反复出现需保持外观、或作为 `prop:<道具名>` 发声时，才写进 道具.md / 正文 `- 道具:`。\n" +
  "  不满足这些条件或拿不准时，不要提升为道具。\n" +
  "  仅仅被拿起、使用、佩戴、看见、扔出、携带、损坏、短暂参与动作，不构成道具登记理由。\n" +
  "  不要为普通物品类别创建“某人的某物”这类专属变体，除非这个实体本身需要跨场连续，\n" +
  "  或带有必须看清的差异化内容。\n" +
  "  普通动作工具、临时手持物、背景摆设、普通生活用品、普通家具、普通服装配件只留在\n" +
  "  action 文本。应抽：可读规则 / 证据物、任务或秘密载体、冲突关键武器、通行钥匙或凭证、\n" +
  "  反复出现的护身符 / 装置、会发声的物件；不应抽：普通通讯设备、工具、餐具、书本、\n" +
  "  衣物、家具、装饰物等一次性动作细节。\n" +
  "- 同类资产名按 canonical key 去重：折叠空白、忽略结构性标点和 ASCII 大小写；如\n" +
  "  `李四 A` / `李四 a` 会合并为首个显示名，其他名字进入 aliases。\n" +
  "- 群体词（众人 / 群众 / 围观者 / 路人们）不进 人物.md，登到 发声源.md 用 group 或写动作旁白。\n" +
  "- 动作 / 对白正文逐字保留，不要转述 / 翻译 / 概括 / 加注释；动作种类只有 act/dlg/think。\n" +
  "- 每个出现在正文里的角色，都要在 人物.md 给出非空描述（否则校验报错）。\n";

export interface CommandOutput<T extends Record<string, unknown> = Record<string, unknown>> {
  /** Exact default-mode text. Commands own its wording and layout. */
  text: string;
  /** Structured command data. JSON mode spreads these fields at the top level. */
  data: T;
}

export interface ValidationIssue {
  code?: string;
  severity?: string;
  summary?: string;
  repair_hint?: string;
  episode?: string;
  scene?: string;
  action_index?: number;
  [key: string]: unknown;
}

export interface RunState {
  status?: string;
  command?: string;
  init_stage?: string;
  provider?: string;
  model?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface Script {
  version?: number;
  title?: string;
  worldview?: string;
  style?: string;
  synopsis?: string;
  theme?: string;
  logline?: string;
  main_characters?: Array<Record<string, unknown>>;
  actors?: Array<Record<string, unknown>>;
  locations?: Array<Record<string, unknown>>;
  props?: Array<Record<string, unknown>>;
  speakers?: Array<Record<string, unknown>>;
  episodes?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface CliErrorOptions {
  exitCode: number;
  errorCode: string;
  hint?: string;
  data?: Record<string, unknown>;
}

export class CliError extends Error {
  readonly exitCode: number;
  readonly errorCode: string;
  readonly hint?: string;
  readonly data: Record<string, unknown>;

  constructor(message: string, opts: CliErrorOptions) {
    super(message);
    this.name = "CliError";
    this.exitCode = opts.exitCode;
    this.errorCode = opts.errorCode;
    this.hint = opts.hint;
    this.data = opts.data ?? {};
  }
}

export function workspaceLayout(workspace: string): WorkspaceLayout {
  return new WorkspaceLayout(workspace);
}

export function directDir(workspace: string): string {
  return workspaceLayout(workspace).directDir;
}

export function scriptJsonPath(opts: Record<string, unknown>): string {
  const v = opts["script_path"] ?? opts["scriptPath"];
  return path.resolve(typeof v === "string" && v.length > 0 ? v : SCRIPT_REL);
}

export function fmtId(prefix: string, n: number): string {
  return `${prefix}_${String(n).padStart(3, "0")}`;
}

export function readJson<T = unknown>(p: string): T {
  return DEFAULT_STORE.readJson<T>(p);
}

export function writeJson(p: string, data: unknown): void {
  DEFAULT_STORE.writeJson(p, data);
}

export function readText(p: string): string {
  return DEFAULT_STORE.readText(p);
}

export function exists(p: string): boolean {
  return DEFAULT_STORE.exists(p);
}

export function writeText(p: string, text: string): void {
  DEFAULT_STORE.writeText(p, text);
}

export function deletePath(p: string): void {
  DEFAULT_STORE.delete(p);
}

export function deleteTree(p: string): void {
  DEFAULT_STORE.deleteTree(p);
}

export function copyFile(src: string, dst: string): void {
  DEFAULT_STORE.copyToPath(src, dst);
}

export function sha256Text(text: string): string {
  return crypto.createHash("sha256").update(text, "utf-8").digest("hex");
}

export function sha256File(p: string): string {
  const digest = crypto.createHash("sha256");
  const fd = fs.openSync(p, "r");
  try {
    const buf = Buffer.alloc(1024 * 1024);
    while (true) {
      const n = fs.readSync(fd, buf, 0, buf.length, null);
      if (n <= 0) break;
      digest.update(buf.subarray(0, n));
    }
  } finally {
    fs.closeSync(fd);
  }
  return digest.digest("hex");
}

export type ArtifactStoreRef = ArtifactStore;
